import { axiosGet } from "../Helpers/AxiosHelper";
import { ABLYINIT } from "../Config/ApiUrl";
import Ably from 'ably';

let ablyClient = null;
let subscriptions = {};

/**
 * Initialize Ably client for task system real-time updates
 */
async function initTaskRealtime() {
  if (ablyClient) {
    return ablyClient;
  }

  try {
    ablyClient = new Ably.Realtime({
      authCallback: async (tokenParams, callback) => {
        try {
          const response = await axiosGet(ABLYINIT);
          if (response?.data?.ably_token) {
            callback(null, response.data.ably_token);
          } else {
            callback(new Error("Invalid token response"), null);
          }
        } catch (error) {
          console.error("[TaskRealtime] Token fetch failed:", error);
          callback(error, null);
        }
      },
    });

    ablyClient.connection.on("stateChange", (stateChange) => {
      if (stateChange.current === "connected") {
        console.log("[TaskRealtime] Connected to Ably");
      } else if (stateChange.current === "disconnected" || stateChange.current === "suspended") {
        console.warn("[TaskRealtime] Disconnected, will reconnect...");
      }
    });

    return ablyClient;
  } catch (error) {
    console.error("[TaskRealtime] Failed to initialize:", error);
    throw error;
  }
}

/**
 * Subscribe to project channel for real-time task updates
 * @param {number} projectId - The project ID to subscribe to
 * @param {object} handlers - Event handlers { onTaskCreated, onTaskUpdated, onTaskDeleted, etc. }
 */
async function subscribeToProject(projectId, handlers = {}) {
  if (!projectId) {
    console.warn("[TaskRealtime] No projectId provided");
    return null;
  }

  const channelName = `task-project-${projectId}`;

  // Already subscribed
  if (subscriptions[channelName]) {
    console.log("[TaskRealtime] Already subscribed to", channelName);
    return subscriptions[channelName];
  }

  try {
    const client = await initTaskRealtime();
    const channel = client.channels.get(channelName);

    // Subscribe to all events
    channel.subscribe((message) => {
      const { name: eventType, data } = message;
      console.log("[TaskRealtime] Received:", eventType, data);

      switch (eventType) {
        case 'task_created':
          handlers.onTaskCreated?.(data);
          break;
        case 'task_updated':
          handlers.onTaskUpdated?.(data);
          break;
        case 'task_deleted':
          handlers.onTaskDeleted?.(data);
          break;
        case 'task_moved':
          handlers.onTaskMoved?.(data);
          break;
        case 'task_archived':
          handlers.onTaskArchived?.(data);
          break;
        case 'comment_added':
          handlers.onCommentAdded?.(data);
          break;
        case 'step_created':
          handlers.onStepCreated?.(data);
          break;
        case 'step_updated':
          handlers.onStepUpdated?.(data);
          break;
        case 'step_deleted':
          handlers.onStepDeleted?.(data);
          break;
        default:
          handlers.onAnyEvent?.(eventType, data);
      }
    });

    subscriptions[channelName] = channel;
    console.log("[TaskRealtime] Subscribed to", channelName);
    return channel;
  } catch (error) {
    console.error("[TaskRealtime] Subscribe failed:", error);
    return null;
  }
}

/**
 * Unsubscribe from project channel
 */
function unsubscribeFromProject(projectId) {
  const channelName = `task-project-${projectId}`;

  if (subscriptions[channelName]) {
    subscriptions[channelName].unsubscribe();
    delete subscriptions[channelName];
    console.log("[TaskRealtime] Unsubscribed from", channelName);
  }
}

/**
 * Unsubscribe from all channels and close connection
 */
function disconnect() {
  Object.keys(subscriptions).forEach(channelName => {
    subscriptions[channelName].unsubscribe();
  });
  subscriptions = {};

  if (ablyClient) {
    ablyClient.close();
    ablyClient = null;
  }
  console.log("[TaskRealtime] Disconnected");
}

/**
 * Check if connected
 */
function isConnected() {
  return ablyClient?.connection?.state === 'connected';
}

export {
  initTaskRealtime,
  subscribeToProject,
  unsubscribeFromProject,
  disconnect,
  isConnected
};
